import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SaveSimulationService {

  // --- BehaviorSubjects for saved values ---
  private savedCustomerId = new BehaviorSubject<number | null>(null);
  private savedCountryId = new BehaviorSubject<number | null>(null);
  private savedRegionId = new BehaviorSubject<number | null>(null);
  private savedProjectId = new BehaviorSubject<number | null>(null);
  private savedSimulationId = new BehaviorSubject<number | null>(null);
  private savedCustomerName = new BehaviorSubject<string | null>(null);
  private savedRegionName = new BehaviorSubject<string | null>(null);
  private savedProjectName = new BehaviorSubject<string | null>(null);
  private savedSimulationName = new BehaviorSubject<string | null>(null);
  private savedVisibility = new BehaviorSubject<string | null>(null);
  private savedNotes = new BehaviorSubject<string | null>(null);
  private countries = new BehaviorSubject<any[] | null>(null);

  constructor() { }

  // --- Getters for saved values ---
  getSavedCustomerId(): number | null {
    return this.savedCustomerId.getValue();
  }

  getSavedCountryId(): number | null {
    return this.savedCountryId.getValue();
  }

  getSavedRegionId(): number | null {
    return this.savedRegionId.getValue();
  }

  getSavedProjectId(): number | null {
    return this.savedProjectId.getValue();
  }

  getSavedSimulationId(): number | null {
    return this.savedSimulationId.getValue();
  }

  getSavedCustomerName(): string | null {
    return this.savedCustomerName.getValue();
  }

  getSavedRegionName(): string | null {
    return this.savedRegionName.getValue();
  }

  getSavedProjectName(): string | null {
    return this.savedProjectName.getValue();
  }

  getSavedSimulationName(): string | null {
    return this.savedSimulationName.getValue();
  }

  getSavedVisibility(): string | null {
    return this.savedVisibility.getValue();
  }

  getSavedNotes(): string | null {
    return this.savedNotes.getValue();
  }

  getCountries(): any[] | null {
    return this.countries.getValue();
  }

  // --- Setters for saved values ---
  setSavedCustomerId(id: number | null) {
    this.savedCustomerId.next(id);
  }

  setSavedRegionId(id: number | null) {
    this.savedRegionId.next(id);
  }

  setSavedProjectId(id: number | null) {
    this.savedProjectId.next(id);
  }

  setSavedSimulationId(id: number | null) {
    this.savedSimulationId.next(id);
  }

  setSavedCustomerName(name: string | null) {
    this.savedCustomerName.next(name || null);
  }

  setSavedRegionName(name: string | null) {
    this.savedRegionName.next(name || null);
  }

  setSavedProjectName(name: string | null) {
    this.savedProjectName.next(name || null);
  }

  setSavedSimulationName(name: string | null) {
    this.savedSimulationName.next(name || null);
  }

  setSavedVisibility(visibility: string | null) {
    this.savedVisibility.next(visibility || null);
  }

  setSavedNotes(notes: string | null) {
    this.savedNotes.next(notes || null);
  }

  setCountries(countries: any[]) {
    this.countries.next(countries);
  }

  /**
   * Set all saved values at once
   */
  setAllSavedValues({
    customerId = null,
    regionId = null,
    projectId = null,
    simulationId = null,
    customerName = null,
    regionName = null,
    projectName = null,
    simulationName = null,
    visibility = null,
    notes = null,
  }: {
    customerId?: number | null,
    regionId?: number | null,
    projectId?: number | null,
    simulationId?: number | null,
    customerName?: string | null,
    regionName?: string | null,
    projectName?: string | null,
    simulationName?: string | null,
    visibility?: string | null,
    notes?: string | null,
  }) {
    this.setSavedCustomerId(customerId);
    this.setSavedRegionId(regionId);
    this.setSavedProjectId(projectId);
    this.setSavedSimulationId(simulationId);
    this.setSavedCustomerName(customerName);
    this.setSavedRegionName(regionName);
    this.setSavedProjectName(projectName);
    this.setSavedSimulationName(simulationName);
    this.setSavedVisibility(visibility);
    this.setSavedNotes(notes);
  }

  // --- Reset method ---
  resetSavedValues() {
    this.savedCustomerId.next(null);
    this.savedCountryId.next(null);
    this.savedRegionId.next(null);
    this.savedProjectId.next(null);
    this.savedSimulationId.next(null);
    this.savedCustomerName.next(null);
    this.savedRegionName.next(null);
    this.savedProjectName.next(null);
    this.savedSimulationName.next(null);
    this.savedVisibility.next(null);
    this.savedNotes.next(null);
  }

  // --- Utility methods ---
  normalizeName(str: string): string {
    return (str || '').trim().toUpperCase();
  }

  createNewEntity(name: string) {
    return { id: null, name: this.normalizeName(name), __isNew__: true };
  }
}